"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { BrandMark } from "@/components/ui/design-system/brand-mark";
import { Button } from "@/components/ui/design-system/button";
import { SURFACE } from "@/components/ui/design-system/surface-styles";
import { Body, CardTitle, Hint } from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";
import { QUIET_HOVER, QUIET_OUTLINE } from "../one/chrome";
import { Pill } from "../one/ui";
import type { BrandBusy, BrandDirectoryEntry } from "./useBrandDirectory";

/**
 * One brand in the list: its mark, its name and rate, the line the brand
 * wrote for creators, and where this creator stands with it.
 *
 * The standing sits beside the name as a badge and the one thing to do about
 * it sits at the card's trailing edge, so a column of cards reads as a list
 * of names first and a list of buttons second.
 */
export function BrandCard({
  brand,
  busy,
  slotsFull,
  applyPending,
  limitId,
  failure,
  onApply,
  onWithdraw,
}: {
  brand: BrandDirectoryEntry;
  busy: BrandBusy;
  /** Every waiting slot is taken, which is the reason `limitId` gives. */
  slotsFull: boolean;
  /** An application to some brand is on its way. */
  applyPending: boolean;
  /** The line saying why Apply is off, for `aria-describedby`. */
  limitId: string;
  /** The server's reason the last action on this brand failed. */
  failure: string | null;
  onApply: () => void;
  onWithdraw: () => void;
}) {
  const t = useTranslations("brands");

  let status: ReactNode = null;
  let action: ReactNode = null;
  if (brand.status === "open") {
    action = (
      <Button
        variant="ink"
        size="sm"
        disabled={slotsFull || applyPending || busy !== null}
        aria-describedby={slotsFull ? limitId : undefined}
        aria-label={t("applyLabel", { brand: brand.name })}
        onClick={onApply}
      >
        {busy === "applying" ? t("applying") : t("apply")}
      </Button>
    );
  } else if (brand.status === "pending") {
    status = <Pill tone="info">{t("status.pending")}</Pill>;
    action = (
      <Button
        variant="ghost"
        size="sm"
        className={QUIET_HOVER}
        disabled={busy !== null}
        aria-label={t("withdrawLabel", { brand: brand.name })}
        onClick={onWithdraw}
      >
        {busy === "withdrawing" ? t("withdrawing") : t("withdraw")}
      </Button>
    );
  } else if (brand.status === "joined") {
    status = <Pill tone="ok">{t("status.joined")}</Pill>;
    action = (
      <Button asChild variant="outline" size="sm" className={QUIET_OUTLINE}>
        <Link
          href={`/one?orgSlug=${encodeURIComponent(brand.orgSlug)}`}
          aria-label={t("openLabel", { brand: brand.name })}
        >
          {t("open")}
        </Link>
      </Button>
    );
  } else {
    status = <Pill tone="idle">{t("status.notAccepted")}</Pill>;
  }

  return (
    <li
      className={cn(
        SURFACE,
        "flex flex-col gap-nf-ds-item p-nf-ds-4 lg:p-nf-ds-6",
      )}
    >
      <div className="flex min-w-0 items-center gap-nf-ds-item">
        <BrandMark
          name={brand.name}
          imageUrl={brand.logoUrl}
          className="size-nf-ds-12 rounded-nf-ds-inset"
          fallbackClassName="bg-nf-ds-tone-brand-bg text-nf-ds-title text-nf-ds-tone-brand-fg"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-nf-ds-1">
          <div className="flex min-w-0 flex-wrap items-center gap-nf-ds-tight">
            <CardTitle as="h3" className="min-w-0 truncate">
              {brand.name}
            </CardTitle>
            {status}
          </div>
          <Hint muted>{t("rate", { share: brand.payoutSpendSharePct })}</Hint>
        </div>
      </div>

      {brand.description ? (
        <Body muted className="text-pretty">
          {brand.description}
        </Body>
      ) : null}

      {action ? <div className="flex justify-end">{action}</div> : null}

      {failure ? (
        // The role sits on the wrapper because the text roles take no `role`.
        <span role="alert">
          <Hint className="text-pretty text-nf-ds-destructive">{failure}</Hint>
        </span>
      ) : null}
    </li>
  );
}
